import {
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { useConfirm } from "material-ui-confirm";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";

import LoadingButton from "../../../../design/wilya-loading-button";
import useTransportData from "../../controllers/use-transport-data";
import {
	createBusStop,
	renameBusStop,
	updateBusStopStatus,
} from "../../store/transport-slice";
import { BusStop } from "../../store/transport-types";

const NAME_MAX_LENGTH = 200;

export interface BusStopFormValues {
	name: string;
	id?: number;
}

interface BusStopFormProps {
	open: boolean;
	onClose: () => void;
	mode: "add" | "edit";
	locationId: number;
	initialData: BusStopFormValues | null;
}

const BusStopForm = ({
	open,
	onClose,
	mode = "add",
	locationId,
	initialData = null,
}: BusStopFormProps) => {
	const dispatch = useDispatch();
	const confirm = useConfirm();
	const {
		busStops,
		createBusStopStatus,
		renameBusStopStatus,
		updateBusStopStatusStatus,
	} = useTransportData();

	const isEditMode = mode === "edit";

	const initialValues: BusStopFormValues = {
		name: "",
		...initialData,
	};

	// Names are unique within a location and the server's check counts
	// deactivated stops too, so the clash is caught here before the round trip.
	// An inactive clash is offered as a reactivation instead (see handleSubmit),
	// which is why only the blocking cases fail validation.
	const findClash = (name: string) =>
		busStops.find(
			(stop: BusStop) =>
				stop.id !== initialData?.id &&
				stop.name.trim().toLowerCase() === name.trim().toLowerCase()
		);

	const validationSchema = Yup.object().shape({
		name: Yup.string()
			.trim()
			.required("Name is required")
			.max(NAME_MAX_LENGTH, `Name must be ${NAME_MAX_LENGTH} characters or less`)
			.test(
				"unique-within-location",
				"A stop with this name already exists at this location",
				(value) => {
					if (!value) {
						return true;
					}
					const clash = findClash(value);
					if (!clash) {
						return true;
					}
					// An inactive clash while adding is recoverable — the form
					// offers to bring the existing stop back instead.
					return !isEditMode && !clash.isActive;
				}
			),
	});

	const handleSubmit = (values: BusStopFormValues) => {
		const name = values.name.trim();
		if (isEditMode && initialData?.id) {
			dispatch(renameBusStop({ id: initialData.id, name, locationId }));
			return;
		}
		const inactiveMatch = findClash(name);
		if (inactiveMatch && !inactiveMatch.isActive) {
			confirm({
				title: "Reactivate stop",
				content: `"${inactiveMatch.name}" already exists at this location but is deactivated. Reactivate it instead of creating a second stop, so past orders keep pointing at the same place.`,
				cancellationText: "Cancel",
				confirmationText: "Reactivate",
			})
				.then(() =>
					dispatch(
						updateBusStopStatus({
							id: inactiveMatch.id,
							isActive: true,
							locationId,
						})
					)
				)
				.catch(() => undefined);
			return;
		}
		dispatch(createBusStop({ locationId, name }));
	};

	useEffect(() => {
		if (
			createBusStopStatus === "fulfilled" ||
			renameBusStopStatus === "fulfilled" ||
			updateBusStopStatusStatus === "fulfilled"
		) {
			onClose();
		}
	}, [
		createBusStopStatus,
		renameBusStopStatus,
		updateBusStopStatusStatus,
		onClose,
	]);

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
			<DialogTitle sx={{ padding: "32px 32px 20px 32px" }}>
				{isEditMode ? "Rename stop" : "Add stop"}
			</DialogTitle>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ errors, touched, dirty }) => (
					<Form>
						<DialogContent sx={{ padding: "20px 32px" }}>
							<Field
								as={TextField}
								fullWidth
								autoFocus
								name='name'
								label='Name'
								variant='filled'
								helperText={
									(touched.name && errors.name) ||
									"Use a landmark workers recognise, not a postal address."
								}
								error={!!(errors.name && touched.name)}
								inputProps={{ maxLength: NAME_MAX_LENGTH }}
							/>
							{isEditMode ? (
								<Alert severity='info' sx={{ marginTop: "20px" }}>
									Renaming changes this stop everywhere, including on rides
									already booked and on past supplier orders. To move the
									collection point somewhere else, deactivate this stop and add
									a new one instead.
								</Alert>
							) : null}
						</DialogContent>
						<DialogActions sx={{ padding: "25px" }}>
							<Button onClick={onClose}>Cancel</Button>
							<LoadingButton
								type='submit'
								variant='contained'
								color='primary'
								disabled={Object.keys(errors).length > 0 || !dirty}
								loading={
									createBusStopStatus === "pending" ||
									renameBusStopStatus === "pending"
								}
							>
								{isEditMode ? "Update" : "Add"}
							</LoadingButton>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default BusStopForm;
